'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import type { ApiSuccessResponse } from '@/shared';
import { API } from '../../config/env';
import { api, ApiError } from '@/lib/api';

type Step = 'request' | 'reset' | 'done';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post<ApiSuccessResponse<{ message: string }>>(API.PASSWORD_RESET_REQUEST, { email: email.trim() });
      setInfo(`A 6-digit code was sent to ${email}. It expires in 15 minutes.`);
      setStep('reset');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please wait 15 minutes before trying again.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Hit a snag. Give it another shot.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otp)) {
      setError('OTP must be 6 digits');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('Password must contain uppercase, lowercase, and a number');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post<ApiSuccessResponse<{ message: string }>>(API.PASSWORD_RESET, {
        email: email.trim(),
        otp,
        newPassword,
      });
      setStep('done');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait and try again.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Hit a snag. Give it another shot.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-12 sm:px-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-7 text-center">
          <Link href="/" className="inline-flex items-center gap-3">
            <Image src="/logo-icon.svg" alt="DiecastShowcase logo" width={44} height={44} className="h-11 w-11" priority />
            <span className="text-2xl font-extrabold tracking-tight text-foreground">DiecastShowcase</span>
          </Link>

          <div className="mx-auto mt-4 flex max-w-[220px] items-center gap-2">
            <span className={`h-1 flex-1 rounded-full ${step === 'request' ? 'bg-primary' : 'bg-primary/35'}`} />
            <span className={`h-1 flex-1 rounded-full ${step === 'reset' ? 'bg-primary' : 'bg-primary/35'}`} />
            <span className={`h-1 flex-1 rounded-full ${step === 'done' ? 'bg-primary' : 'bg-primary/35'}`} />
          </div>

          <h1 className="mt-6 text-3xl font-bold text-foreground">
            {step === 'request' && 'Forgot password?'}
            {step === 'reset' && 'Reset your password'}
            {step === 'done' && 'Password updated'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {step === 'request' && 'Enter your email and we will send a reset code.'}
            {step === 'reset' && 'Enter your code and set a new password.'}
            {step === 'done' && 'You can now sign in with your new password.'}
          </p>
        </div>

        <div className="card-shell p-6 sm:p-7">
          {step === 'done' && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <Link href="/login" className="btn-primary w-full justify-center">
                Go to sign in
              </Link>
            </div>
          )}

          {step === 'request' && (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              {error && <div className="rounded-lg border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
              <div>
                <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  className="form-input"
                  required
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Sending code...' : 'Send reset code'}
              </button>
            </form>
          )}

          {step === 'reset' && (
            <form onSubmit={handleReset} className="space-y-5">
              {info && <div className="rounded-lg border border-primary/35 bg-primary/10 px-4 py-3 text-sm text-primary">{info}</div>}
              {error && <div className="rounded-lg border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

              <div>
                <label htmlFor="otp" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">6-digit code</label>
                <input
                  id="otp"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError('');
                  }}
                  className="form-input text-center font-mono tracking-[0.25em]"
                  maxLength={6}
                  required
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">New password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  className="form-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Confirm password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  className="form-input"
                  required
                />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Resetting...' : 'Reset password'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setError('');
                  setInfo('');
                  setOtp('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-full text-sm font-medium text-muted-foreground"
              >
                Back to email entry
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-secondary-foreground">
          Remembered it?{' '}
          <Link href="/login" className="font-semibold text-primary">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

